import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import {
  isValidPayeReference,
  isValidCompanyNumber,
  isValidAccountsOfficeReference,
  PAYE_REFERENCE_INVALID_MESSAGE,
  PAYE_REFERENCE_MAX_LENGTH,
  ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE,
  ACCOUNTS_OFFICE_REFERENCE_MAX_LENGTH,
} from '../utils/orgComplianceValidation';
import {
  normalizeAvailableRoles,
  isSubcontractorOnlyShell,
  hasSubcontractorDashboardAccess,
  showEmployerSubcontractorDashboardSwitch,
  isEmployerDashboardRoleActive,
} from '../utils/userDashboardUi';
import { useProfileShellDashboardNav } from '../hooks/useProfileShellDashboardNav';
import ProfileUsersTab from './ProfileUsersTab';
import './SubcontractorProfile.css';

const SubcontractorProfile = ({ user }) => {
  const navigate = useNavigate();
  
  // Helper function to check if user is an employer
  const isEmployer = () => {
    if (!localUser) return false;
    if (isSubcontractorOnlyShell(localUser)) return false;
    const roles = normalizeAvailableRoles(localUser);
    if (localUser.current_role === 'contractor' || localUser.current_role === 'both') {
      return true;
    }
    if (roles.includes('contractor')) {
      return true;
    }
    if (localUser.role === 'employer_admin' && roles.includes('contractor')) {
      return true;
    }
    return false;
  };

  /** Subcontractor org fields (incl. RC preference) — includes dual employer+subcontractor accounts. */
  const hasSubcontractorOrgSettingsAccess = () => {
    if (!localUser) return false;
    if (isSubcontractorOnlyShell(localUser)) return true;
    if (!isEmployer()) return true;
    if (hasSubcontractorDashboardAccess(localUser)) return true;
    const roles = normalizeAvailableRoles(localUser);
    const currentRole = String(localUser.current_role || '').toLowerCase();
    return currentRole === 'both' || roles.includes('subcontractor');
  };
  
  const [profileData, setProfileData] = useState({
    forename: '',
    surname: '',
    email: '',
    phone_number: '',
    address: '',
    postcode: '',
    unique_tax_reference: '',
    // cis_rate removed - now handled by HMRC verification
    day_rate: '',
    trade: '',
    trade_type: '',
    trade_type_other: '',
    company_name: '',
    building_name_no: '',
    street: '',
    vat_number: '',
    ni_number: '',
    // Banking information - Legacy fields
    bank_account_name: '',
    bank_account_number: '',
    bank_sort_code: '',
    // Banking information - Preferred fields (matching specification)
    account_holder_name: '',
    sort_code: '',
    account_number: '',
    verified: false,
    // Employer fields
    business_type: '',
    cis_liable: '',
    self_employed: '',
    registered_name: '',
    company_type: '',
    paye_reference: '',  // PAYE Reference - per-user field
    accounts_office_reference: '',
    // HMRC auto-settings (for employers)
    auto_verify_cis: true,
    auto_submit_returns: false,
    // Government Gateway (employers — loaded via GET /organization when on profile)
    hmrc_gateway_user_id: '',
    hmrc_gateway_password: '',
    /** Subcontractor-facing contract terms (employer org); shown on connect / review & accept */
    contract_terms: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [hmrcTestLoading, setHmrcTestLoading] = useState(false);
  const [hmrcGatewaySaving, setHmrcGatewaySaving] = useState(false);
  const [hmrcAutoSettingsSaving, setHmrcAutoSettingsSaving] = useState(false);
  const [showHmrcGatewayPassword, setShowHmrcGatewayPassword] = useState(false);
  const [dashboardRoleSwitching, setDashboardRoleSwitching] = useState(false);
  
  // VAT verification states
  const [vatValidating, setVatValidating] = useState(false);
  const [vatValidated, setVatValidated] = useState(false);
  const [vatValidationError, setVatValidationError] = useState('');
  const [vatValidationSuccess, setVatValidationSuccess] = useState(false);
  const [originalVatNumber, setOriginalVatNumber] = useState(''); // Track original VAT to detect changes
  const [niValidationError, setNiValidationError] = useState('');
  const [utrValidationError, setUtrValidationError] = useState('');
  const [payeReferenceValidationError, setPayeReferenceValidationError] = useState('');
  const [accountsOfficeReferenceValidationError, setAccountsOfficeReferenceValidationError] = useState('');
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const normaliseNiNumber = (value) => (value || '').toUpperCase().replace(/\s+/g, '');
  const isValidNiNumber = (value) =>
    /^[A-CEGHJ-NPR-TW-Z]{2}\d{6}[A-D]$/.test(value) && value !== 'AA000000A';

  // Local user state that syncs with prop and localStorage
  const [localUser, setLocalUser] = useState(user);
  useProfileShellDashboardNav(localUser);

  const location = useLocation();
  const pathNorm = (location.pathname || '/').replace(/\/$/, '') || '/';
  const profileSection =
    pathNorm === '/profile/organization'
      ? 'organization'
      : pathNorm === '/profile/users'
        ? 'users'
        : 'personal';

  const isMainEmployerUser = () => {
    if (!localUser) return false;
    return localUser.role !== 'bookkeeper' && localUser.role !== 'manager';
  };

  const hasUserPermission = (permissionKey) => {
    if (isMainEmployerUser()) return true;
    return Boolean(localUser?.permissions?.[permissionKey]);
  };

  const canViewUsersTab = Boolean(isEmployer() && hasUserPermission('view_users'));
  const canAddUsers = Boolean(isEmployer() && hasUserPermission('add_users'));
  const canDeleteUsers = Boolean(isEmployer() && isMainEmployerUser());

  // Sync localUser with prop when it changes
  useEffect(() => {
    if (user) {
      console.log('🔍 [PAYE DEBUG] user prop changed:', user);
      console.log('🔍 [PAYE DEBUG] user.paye_reference:', user.paye_reference);
      setLocalUser(user);
    }
  }, [user]);

  useEffect(() => {
    if (!localUser) return;
    if (profileSection === 'users' && !canViewUsersTab) {
      navigate('/profile', { replace: true });
    }
  }, [profileSection, localUser, canViewUsersTab, navigate]);

  useEffect(() => {
    // Load profile data from user object
    if (localUser) {
      console.log('🔍 [PAYE DEBUG] useEffect triggered, localUser:', localUser);
      console.log('🔍 [PAYE DEBUG] organization.paye_reference:', localUser.organization?.paye_reference, 'type:', typeof localUser.organization?.paye_reference);
      
      // Split full_name into forename and surname if needed
      const nameParts = (localUser.full_name || '').split(' ');
      const forename = nameParts[0] || '';
      const surname = nameParts.slice(1).join(' ') || '';
      
      // Parse address if it's a multi-line string
      const orgData = localUser.organization || {};
      const addressLines = (orgData.address || '').split('\n');
      
      // Track original VAT number (org payload, then top-level /me fields)
      const currentVatNumber =
        orgData.vat_number_form ||
        orgData.vat_number ||
        localUser.vat_number_form ||
        localUser.vat_number ||
        '';
      setOriginalVatNumber(currentVatNumber);
      // If user already has a VAT number, consider it validated
      if (currentVatNumber) {
        setVatValidated(true);
      }
      
      const payeRefValue = orgData.paye_reference || '';
      console.log('🔍 [PAYE DEBUG] payeRefValue to be set:', payeRefValue, 'type:', typeof payeRefValue);
      
      const utrDigitsFromUser = String(
        orgData.utr || orgData.unique_tax_reference || ''
      )
        .replace(/\D/g, '')
        .slice(0, 10);
      const newProfileData = {
        forename: forename,
        surname: surname,
        email: localUser.email || '',
        phone_number: localUser.phone_number || '',
        address: orgData.address || '',
        address_line1: addressLines[0] || '',
        address_line2: addressLines[1] || '',
        address_line3: addressLines[2] || '',
        address_line4: addressLines[3] || '',
        address_line5: addressLines[4] || '',
        postcode: orgData.postal_code || '',
        city: orgData.city || '',
        unique_tax_reference: utrDigitsFromUser,
        utr: utrDigitsFromUser,
        company_number: String(orgData.company_number || '')
          .replace(/\D/g, '')
          .slice(0, 8),
        // cis_rate removed - now handled by HMRC verification
        day_rate: orgData.day_rate ? orgData.day_rate.toString() : '',
        trade: orgData.trade || '',
        trade_type: (() => {
          // List of standard trade types from registration
          const standardTradeTypes = [
            'Architect / Designer / Surveyor (consultancy only)',
            'Asbestos remover',
            'Bathroom fitter',
            'Brick / Block layer',
            'Canteen operator / Welfare provider',
            'Carpenter / Joiner',
            'Civil engineer (on-site works)',
            'Cladder / Curtain wall installer',
            'Cleaner (construction / builders\' clean)',
            'Cleaner (domestic / maintenance)',
            'Concrete worker / Paver / Kerber',
            'Crane hire (without labour)',
            'Crane operator (with labour)',
            'Delivery driver / Haulage',
            'Demolition worker',
            'Drainage / Pipework installer (external)',
            'Electrician',
            'Engineer (consulting only)',
            'Fire alarm / Security system installer',
            'Floor fitter / Carpet layer',
            'General contractor / Builder',
            'Ground investigation / Borehole driller',
            'Groundworker / Excavator / Digger driver',
            'HVAC installer / Ventilation engineer',
            'Insulation installer',
            'Kitchen fitter',
            'Landscaper (hard landscaping)',
            'Landscaper (soft landscaping only)',
            'Leadworker',
            'Lift / Elevator installer',
            'Painter / Decorator',
            'Pest control / Environmental cleaning',
            'Plant hire (without operator)',
            'Plant operator (with labour)',
            'Plasterer / Plasterboard fixer / Dry liner / Taper & jointer',
            'Plumber / Heating engineer',
            'Roofer',
            'Scaffold hire (no labour)',
            'Scaffolder (with labour)',
            'Sign fitter / Fascia installer',
            'Site Manager / Foreman (self-employed)',
            'Site security / Watchman',
            'Steel fixer / Fabricator / Welder (on site)',
            'Supplier of materials only',
            'Telecoms / Data installer',
            'Temporary works / Site hoarding erector',
            'Tiler (wall / floor / roof)',
            'Tree surgeon / Arborist',
            'Waste removal (with labour / site clearance)',
            'Waste removal / Skip hire (no labour)',
            'Window fitter / Glazier'
          ];
          
          const userTradeType = orgData.trade_type || '';
          // If the user's trade_type is not in the standard list, it's a custom value
          if (userTradeType && !standardTradeTypes.includes(userTradeType)) {
            return 'Other (please specify)';
          }
          return userTradeType;
        })(),
        trade_type_other: (() => {
          // If trade_type is not in standard list, use it as the "other" value
          const standardTradeTypes = [
            'Architect / Designer / Surveyor (consultancy only)',
            'Asbestos remover',
            'Bathroom fitter',
            'Brick / Block layer',
            'Canteen operator / Welfare provider',
            'Carpenter / Joiner',
            'Civil engineer (on-site works)',
            'Cladder / Curtain wall installer',
            'Cleaner (construction / builders\' clean)',
            'Cleaner (domestic / maintenance)',
            'Concrete worker / Paver / Kerber',
            'Crane hire (without labour)',
            'Crane operator (with labour)',
            'Delivery driver / Haulage',
            'Demolition worker',
            'Drainage / Pipework installer (external)',
            'Electrician',
            'Engineer (consulting only)',
            'Fire alarm / Security system installer',
            'Floor fitter / Carpet layer',
            'General contractor / Builder',
            'Ground investigation / Borehole driller',
            'Groundworker / Excavator / Digger driver',
            'HVAC installer / Ventilation engineer',
            'Insulation installer',
            'Kitchen fitter',
            'Landscaper (hard landscaping)',
            'Landscaper (soft landscaping only)',
            'Leadworker',
            'Lift / Elevator installer',
            'Painter / Decorator',
            'Pest control / Environmental cleaning',
            'Plant hire (without operator)',
            'Plant operator (with labour)',
            'Plasterer / Plasterboard fixer / Dry liner / Taper & jointer',
            'Plumber / Heating engineer',
            'Roofer',
            'Scaffold hire (no labour)',
            'Scaffolder (with labour)',
            'Sign fitter / Fascia installer',
            'Site Manager / Foreman (self-employed)',
            'Site security / Watchman',
            'Steel fixer / Fabricator / Welder (on site)',
            'Supplier of materials only',
            'Telecoms / Data installer',
            'Temporary works / Site hoarding erector',
            'Tiler (wall / floor / roof)',
            'Tree surgeon / Arborist',
            'Waste removal (with labour / site clearance)',
            'Waste removal / Skip hire (no labour)',
            'Window fitter / Glazier'
          ];
          
          const userTradeType = orgData.trade_type || '';
          if (userTradeType && !standardTradeTypes.includes(userTradeType)) {
            return userTradeType;
          }
          return '';
        })(),
        company_name: orgData.company_name_sub || '',
        building_name_no: orgData.building_name_no || '',
        street: orgData.street || '',
        city: orgData.city || '',
        vat_number: orgData.vat_number || localUser.vat_number || '',
        vat_number_form: orgData.vat_number_form || orgData.vat_number || localUser.vat_number_form || localUser.vat_number || '',
        vat_registered: (() => {
          const r = (orgData.vat_registered || '').trim();
          if (r === 'Yes' || r === 'No') return r;
          if (!isEmployer()) return orgData.vat_registered || '';
          const hasVat = `${orgData.vat_number_form || orgData.vat_number || ''}`.trim();
          return hasVat ? 'Yes' : 'No';
        })(),
        // vat_rate removed - using default UK rate (20%)
        // NI number can come from org profile or (legacy) top-level user payload, depending on API shape.
        ni_number: orgData.ni_number || localUser.ni_number || '',
        // Banking information - Legacy fields
        bank_account_name: orgData.bank_account_name || '',
        bank_account_number: orgData.bank_account_number || '',
        bank_sort_code: orgData.bank_sort_code || '',
        // Banking information - Preferred fields (matching specification)
        account_holder_name: orgData.account_holder_name || orgData.bank_account_name || '',
        sort_code: orgData.sort_code || orgData.bank_sort_code || '',
        account_number: orgData.account_number || orgData.bank_account_number || '',
        verified: localUser.verified !== undefined ? localUser.verified : false,
        // Employer fields
        business_type: orgData.business_type || '',
        cis_liable: orgData.cis_liable || '',
        self_employed: (() => {
          const se = (orgData.self_employed ?? localUser.self_employed ?? '').trim();
          if (se === 'Yes' || se === 'No') return se;
          return '';
        })(),
        registered_name: orgData.organization_name || '',
        company_type: orgData.company_type || '',
        paye_reference: payeRefValue,  // PAYE Reference is per-user
        accounts_office_reference: orgData.accounts_office_reference || '',
        // Gateway credentials filled by GET /organization fetch (employers only)
        hmrc_gateway_user_id: '',
        hmrc_gateway_password: '',
        // HMRC auto-settings
        auto_verify_cis: localUser.auto_verify_cis !== undefined ? localUser.auto_verify_cis : true,
        auto_submit_returns: localUser.auto_submit_returns !== undefined ? localUser.auto_submit_returns : false,
        contract_terms: orgData.contract_terms || '',
        subcontractor_reverse_charge_default: !!orgData.subcontractor_reverse_charge_default,
        employer_reverse_charge_default: !!orgData.employer_reverse_charge_default,
      };
      
      // console.log('🔍 [PAYE DEBUG] newProfileData.paye_reference:', newProfileData.paye_reference);
      // console.log('🔍 [PAYE DEBUG] Full newProfileData keys:', Object.keys(newProfileData));
      
      setProfileData(newProfileData);
      
      // console.log('🔍 [PAYE DEBUG] setProfileData called with paye_reference:', newProfileData.paye_reference);
    }
    setLoading(false);
  }, [localUser]);

  // Load decrypted Government Gateway credentials for employers (GET /organization only — not on /me).
  useEffect(() => {
    if (!localUser || isSubcontractorOnlyShell(localUser)) return;
    const roles = normalizeAvailableRoles(localUser);
    const isEmployerUser =
      localUser.current_role === 'contractor' ||
      localUser.current_role === 'both' ||
      roles.includes('contractor');
    if (!isEmployerUser) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl(API_CONFIG.endpoints.getOrganizationProfile)
        );
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (cancelled) return;
        setProfileData((prev) => ({
          ...prev,
          hmrc_gateway_user_id: (data.hmrc_gateway_user_id || '').trim(),
          hmrc_gateway_password: (data.hmrc_gateway_password || '').trim(),
          ni_number: data.ni_number || prev.ni_number || '',
        }));
      } catch {
        /* leave fields empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [localUser]);

  // Debug: Track profileData changes
  useEffect(() => {
    // console.log('🔍 [PAYE DEBUG] profileData changed:', profileData);
    // console.log('🔍 [PAYE DEBUG] profileData.paye_reference:', profileData.paye_reference, 'type:', typeof profileData.paye_reference);
  }, [profileData]);

  // Listen for user-updated events to refresh profile data
  useEffect(() => {
    const handleUserUpdated = () => {
      // console.log('🔍 [PAYE DEBUG] handleUserUpdated event triggered');
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          // console.log('🔍 [PAYE DEBUG] userData from localStorage:', userData);
          // console.log('🔍 [PAYE DEBUG] userData.paye_reference:', userData.paye_reference);
          // Update local user state with fresh data from localStorage
          setLocalUser(userData);
          // Profile data will be updated by the useEffect that depends on localUser
        } catch (err) {
          console.error('Error parsing user data from localStorage:', err);
        }
      }
    };
    
    window.addEventListener('user-updated', handleUserUpdated);
    return () => window.removeEventListener('user-updated', handleUserUpdated);
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    if (name === 'paye_reference') {
      // console.log('🔍 [PAYE DEBUG] handleInputChange - paye_reference changed:', inputValue);
      // console.log('🔍 [PAYE DEBUG] Previous profileData.paye_reference:', profileData.paye_reference);
    }
    
    const nextInputValue =
      name === 'ni_number'
        ? normaliseNiNumber(inputValue)
        : name === 'utr'
          ? String(inputValue || '').replace(/\D/g, '').slice(0, 10)
          : inputValue;
    setProfileData(prev => {
      let newData = {
        ...prev,
        [name]: nextInputValue
      };
      if (name === 'vat_registered' && nextInputValue === 'No' && isEmployer()) {
        newData = {
          ...newData,
          vat_number: '',
          vat_number_form: '',
        };
      }
      if (name === 'paye_reference') {
        // console.log('🔍 [PAYE DEBUG] New profileData.paye_reference:', newData.paye_reference);
      }
      return newData;
    });

    if (name === 'vat_registered' && nextInputValue === 'No' && isEmployer()) {
      setVatValidated(false);
      setVatValidationError('');
      setVatValidationSuccess(false);
    }

    if (name === 'ni_number') {
      if (!nextInputValue) {
        setNiValidationError('');
      } else if (!isValidNiNumber(nextInputValue)) {
        setNiValidationError('Invalid NI Number format. Use AB123456C');
      } else {
        setNiValidationError('');
      }
    }

    if (name === 'utr') {
      if (!nextInputValue) {
        setUtrValidationError('');
      } else if (nextInputValue.length !== 10) {
        setUtrValidationError('UTR must be exactly 10 digits');
      } else {
        setUtrValidationError('');
      }
    }

    if (name === 'paye_reference') {
      setPayeReferenceValidationError('');
    }
    if (name === 'accounts_office_reference') {
      setAccountsOfficeReferenceValidationError('');
    }

    if (isEmployer() && profileSection === 'organization' && (name === 'auto_verify_cis' || name === 'auto_submit_returns')) {
      const nextAutoVerify = name === 'auto_verify_cis'
        ? Boolean(nextInputValue)
        : (profileData.auto_verify_cis !== undefined ? profileData.auto_verify_cis : true);
      const nextAutoSubmit = name === 'auto_submit_returns'
        ? Boolean(nextInputValue)
        : Boolean(profileData.auto_submit_returns);
      void saveHmrcAutomationSettings(nextAutoVerify, nextAutoSubmit);
    }

    // If VAT number is being changed, reset validation
    if (name === 'vat_number_form' || name === 'vat_number') {
      if (value !== originalVatNumber) {
        setVatValidated(false);
        setVatValidationError('');
        setVatValidationSuccess(false);
      } else {
        // If user reverts to original VAT, consider it validated
        setVatValidated(true);
      }
    }
    
    // Clear success message when user starts editing
    if (success) {
      setSuccess('');
    }
  };

  const buildOrganizationSubmitData = () => {
    const addressParts = [
      profileData.address_line1,
      profileData.address_line2,
      profileData.address_line3,
      profileData.address_line4,
      profileData.address_line5
    ].filter(part => part && part.trim());

    const isEmployerOrgSettings = isEmployer() && profileSection === 'organization';
    const showSubCisSelfEmployedFields = hasSubcontractorDashboardAccess(localUser);
    let vatNumberForSubmit = profileData.vat_number_form || profileData.vat_number || null;
    let vatRegisteredForSubmit = profileData.vat_registered || null;
    if (isEmployerOrgSettings) {
      if (String(profileData.vat_registered || '').trim() === 'Yes') {
        vatRegisteredForSubmit = 'Yes';
      } else {
        vatRegisteredForSubmit = 'No';
        vatNumberForSubmit = null;
      }
    }

    return {
      business_type: profileData.business_type || null,
      organization_name: profileData.registered_name || null,
      company_type: profileData.company_type || null,
      company_number: profileData.company_number && profileData.company_number.trim() ? profileData.company_number.trim() : null,
      utr: profileData.utr && profileData.utr.trim() ? profileData.utr.trim() : null,
      unique_tax_reference: profileData.utr && profileData.utr.trim() ? profileData.utr.trim() : null,
      ni_number: profileData.ni_number && profileData.ni_number.trim() ? profileData.ni_number.trim() : null,
      paye_reference: profileData.paye_reference && typeof profileData.paye_reference === 'string' && profileData.paye_reference.trim()
        ? profileData.paye_reference.trim()
        : null,
      accounts_office_reference:
        profileData.accounts_office_reference &&
        typeof profileData.accounts_office_reference === 'string' &&
        profileData.accounts_office_reference.trim()
          ? profileData.accounts_office_reference.trim().toUpperCase().replace(/\s+/g, '')
          : null,
      trade_type: profileData.trade_type === 'Other (please specify)'
        ? (profileData.trade_type_other || null)
        : (profileData.trade_type || null),
      building_name_no: profileData.building_name_no ? profileData.building_name_no.trim() : null,
      street: profileData.street ? profileData.street.trim() : null,
      city: profileData.city || null,
      postal_code: profileData.postcode || null,
      address: addressParts.length > 0 ? addressParts.join('\n') : null,
      vat_number: vatNumberForSubmit,
      vat_number_form: vatNumberForSubmit,
      vat_registered: vatRegisteredForSubmit,
      ...(showSubCisSelfEmployedFields
        ? {
            cis_liable: profileData.cis_liable || null,
            self_employed: profileData.self_employed || null,
          }
        : {}),
      bank_account_name: profileData.bank_account_name || profileData.account_holder_name || null,
      bank_sort_code: (profileData.bank_sort_code ?? profileData.sort_code ?? '').toString().trim() || null,
      bank_account_number: (profileData.bank_account_number ?? profileData.account_number ?? '').toString().trim() || null,
      account_holder_name: profileData.account_holder_name || profileData.bank_account_name || null,
      sort_code: (profileData.sort_code ?? profileData.bank_sort_code ?? '').toString().trim() || null,
      account_number: (profileData.account_number ?? profileData.bank_account_number ?? '').toString().trim() || null,
      subcontractor_reverse_charge_default: hasSubcontractorOrgSettingsAccess()
        ? !!profileData.subcontractor_reverse_charge_default
        : undefined,
      employer_reverse_charge_default: isEmployer()
        ? !!profileData.employer_reverse_charge_default
        : undefined,
      contract_terms: typeof profileData.contract_terms === 'string'
        ? profileData.contract_terms.trim() || null
        : null,
    };
  };

  const saveOrganizationSection = async (sectionKey) => {
    const errors = validateProfile();
    const sectionErrorKeys = {
      organization_details: [
        'company_number',
        'utr',
        'ni_number',
        ...(isEmployer() ? ['paye_reference', 'accounts_office_reference'] : []),
      ],
      tax_information: ['paye_reference', 'accounts_office_reference', 'utr', 'ni_number'],
      banking_details: [],
      contract_terms: [],
      all: Object.keys(errors),
    };
    const relevantKeys = sectionErrorKeys[sectionKey] || [];
    const relevantErrors = Object.fromEntries(
      Object.entries(errors).filter(([k]) => relevantKeys.includes(k))
    );
    if (Object.keys(relevantErrors).length > 0) {
      const firstMsg = Object.values(relevantErrors)[0];
      setError(firstMsg || 'Please fix the validation errors');
      if (window.showToast) {
        window.showToast(firstMsg || 'Please fix the validation errors', 'error', 5000);
      }
      return;
    }

    if (sectionKey === 'tax_information') {
      const employerVatLiable =
        isEmployer() && String(profileData.vat_registered || '').trim() === 'Yes';
      if (employerVatLiable) {
        const currentVatNumber = profileData.vat_number_form || profileData.vat_number || '';
        if (currentVatNumber && currentVatNumber !== originalVatNumber && !vatValidated) {
          setError('Please verify your VAT number by clicking the "Verify VAT" button before saving.');
          if (window.showToast) {
            window.showToast('Please verify your VAT number before saving', 'error', 4000);
          }
          return;
        }
      }
    }

    const orgData = buildOrganizationSubmitData();
    const showSubCisSelfEmployedFields = hasSubcontractorDashboardAccess(localUser);
    const sectionPayloadMap = {
      organization_details: {
        business_type: orgData.business_type,
        organization_name: orgData.organization_name,
        company_type: orgData.company_type,
        company_number: orgData.company_number,
        trade_type: orgData.trade_type,
        building_name_no: orgData.building_name_no,
        street: orgData.street,
        city: orgData.city,
        postal_code: orgData.postal_code,
        address: orgData.address,
        utr: orgData.utr,
        unique_tax_reference: orgData.unique_tax_reference,
        ni_number: orgData.ni_number,
        ...(isEmployer()
          ? {
              paye_reference: orgData.paye_reference,
              accounts_office_reference: orgData.accounts_office_reference,
            }
          : {}),
      },
      tax_information: {
        utr: orgData.utr,
        unique_tax_reference: orgData.unique_tax_reference,
        ni_number: orgData.ni_number,
        paye_reference: orgData.paye_reference,
        accounts_office_reference: orgData.accounts_office_reference,
        vat_number: orgData.vat_number,
        vat_number_form: orgData.vat_number_form,
        vat_registered: orgData.vat_registered,
        ...(showSubCisSelfEmployedFields
          ? {
              cis_liable: profileData.cis_liable || null,
              self_employed: profileData.self_employed || null,
            }
          : {}),
        ...(hasSubcontractorOrgSettingsAccess()
          ? { subcontractor_reverse_charge_default: !!profileData.subcontractor_reverse_charge_default }
          : {}),
        ...(isEmployer()
          ? { employer_reverse_charge_default: !!profileData.employer_reverse_charge_default }
          : {}),
      },
      banking_details: {
        bank_account_name: orgData.bank_account_name,
        bank_sort_code: orgData.bank_sort_code,
        bank_account_number: orgData.bank_account_number,
        account_holder_name: orgData.account_holder_name,
        sort_code: orgData.sort_code,
        account_number: orgData.account_number,
      },
      contract_terms: {
        contract_terms: orgData.contract_terms,
      },
      all: orgData,
    };

    const payload = sectionPayloadMap[sectionKey] || sectionPayloadMap.all;
    setSaving(true);
    setError('');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl('/unified-auth/organization'),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = data?.detail || 'Failed to save organization section';
        throw new Error(Array.isArray(msg) ? msg.map((x) => x.msg || x).join(', ') : msg);
      }
      const freshUserResponse = await API_CONFIG.fetchWithAuth(API_CONFIG.getApiUrl('/unified-auth/me'));
      if (freshUserResponse.ok) {
        const freshUserData = await freshUserResponse.json();
        localStorage.setItem('user', JSON.stringify(freshUserData));
        setLocalUser(freshUserData);
        window.dispatchEvent(new Event('user-updated'));
      }
      const okMsg = 'Section saved successfully.';
      setSuccess(okMsg);
      if (window.showToast) window.showToast(okMsg, 'success', 3000);
    } catch (err) {
      setError(err.message || 'Failed to save section');
      if (window.showToast) window.showToast(err.message || 'Failed to save section', 'error', 4000);
    } finally {
      setSaving(false);
    }
  };

  const saveHmrcAutomationSettings = async (autoVerify, autoSubmit) => {
    setHmrcAutoSettingsSaving(true);
    try {
      const settingsResponse = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl('/unified-auth/hmrc/save-settings'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auto_verify_cis: autoVerify,
            auto_submit_returns: autoSubmit
          })
        }
      );
      if (!settingsResponse.ok) {
        const settingsError = await settingsResponse.json().catch(() => ({}));
        throw new Error(settingsError?.detail || 'Failed to save HMRC automation settings');
      }
    } catch (err) {
      if (window.showToast) {
        window.showToast(err.message || 'Failed to save HMRC automation settings', 'error', 4000);
      }
    } finally {
      setHmrcAutoSettingsSaving(false);
    }
  };


  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateVAT = async (vatNumber) => {
    if (!vatNumber || vatNumber.trim() === '') {
      setVatValidationError('VAT number is required');
      setVatValidated(false);
      return;
    }
    
    setVatValidating(true);
    setVatValidationError('');
    setVatValidationSuccess(false);
    setVatValidated(false);
    
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl('/unified-auth/validate-vat'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ vat_number: vatNumber })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          const savedVat = data.vat_number || vatNumber.trim();
          setVatValidationSuccess(true);
          setVatValidationError('');
          setVatValidated(true);
          setOriginalVatNumber(savedVat);
          setProfileData((prev) => ({
            ...prev,
            vat_number: savedVat,
            vat_number_form: savedVat,
            vat_registered: data.vat_registered || prev.vat_registered || 'Yes',
          }));
          try {
            const freshUserResponse = await API_CONFIG.fetchWithAuth(
              API_CONFIG.getApiUrl('/unified-auth/me')
            );
            if (freshUserResponse.ok) {
              const freshUserData = await freshUserResponse.json();
              localStorage.setItem('user', JSON.stringify(freshUserData));
              setLocalUser(freshUserData);
              window.dispatchEvent(new Event('user-updated'));
            }
          } catch (refreshErr) {
            console.error('VAT saved but failed to refresh user profile:', refreshErr);
          }
          if (window.showToast) {
            window.showToast('VAT number validated and saved successfully!', 'success', 3000);
          }
        } else {
          setVatValidationError(data.message || 'Invalid VAT number. Please check and try again.');
          setVatValidationSuccess(false);
          setVatValidated(false);
          if (window.showToast) {
            window.showToast(data.message || 'Invalid VAT number', 'error', 4000);
          }
        }
      } else {
        const errorData = await response.json();
        setVatValidationError(errorData.detail || 'Failed to validate VAT number');
        setVatValidationSuccess(false);
        setVatValidated(false);
        if (window.showToast) {
          window.showToast('Failed to validate VAT number', 'error', 4000);
        }
      }
    } catch (err) {
      console.error('Error validating VAT:', err);
      setVatValidationError('Error validating VAT number. Please try again.');
      setVatValidationSuccess(false);
      setVatValidated(false);
      if (window.showToast) {
        window.showToast('Error validating VAT number', 'error', 4000);
      }
    } finally {
      setVatValidating(false);
    }
  };

  const validateProfile = () => {
    const errors = {};

    // Only validate email format if provided
    if (profileData.email && !/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = 'Email is invalid';
    }

    if (profileData.day_rate && parseFloat(profileData.day_rate) < 0) {
      errors.day_rate = 'Day rate must be 0 or greater';
    }

    if (profileData.ni_number && profileData.ni_number.trim() && !isValidNiNumber(normaliseNiNumber(profileData.ni_number))) {
      errors.ni_number = 'Invalid NI Number format. Use AB123456C';
    }

    const utrDigits = String(profileData.utr || '').replace(/\D/g, '');
    if (utrDigits.length > 0 && utrDigits.length !== 10) {
      errors.utr = 'UTR must be exactly 10 digits';
    }

    if (
      profileData.company_number &&
      String(profileData.company_number).trim() &&
      !isValidCompanyNumber(profileData.company_number)
    ) {
      errors.company_number = 'Company number must be 1–8 digits only';
    }

    if (isEmployer() && profileSection === 'organization') {
      const paye = String(profileData.paye_reference || '').trim();
      if (!paye) {
        errors.paye_reference = 'PAYE reference is required (e.g. 914/VA11166).';
      } else if (!isValidPayeReference(paye)) {
        errors.paye_reference = PAYE_REFERENCE_INVALID_MESSAGE;
      }
      const ao = String(profileData.accounts_office_reference || '').trim();
      if (!ao) {
        errors.accounts_office_reference =
          'Accounts Office reference is required (e.g. 914PV00196767).';
      } else if (!isValidAccountsOfficeReference(ao)) {
        errors.accounts_office_reference = ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE;
      }
    } else {
      if (
        profileData.paye_reference &&
        String(profileData.paye_reference).trim() &&
        !isValidPayeReference(profileData.paye_reference)
      ) {
        errors.paye_reference = PAYE_REFERENCE_INVALID_MESSAGE;
      }
      if (
        profileData.accounts_office_reference &&
        String(profileData.accounts_office_reference).trim() &&
        !isValidAccountsOfficeReference(profileData.accounts_office_reference)
      ) {
        errors.accounts_office_reference = ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE;
      }
    }

    // VAT rate validation removed - using default UK rate (20%)

    return errors;
  };

  const validatePassword = () => {
    const errors = {};

    if (!passwordData.current_password) {
      errors.current_password = 'Current password is required';
    }

    if (!passwordData.new_password) {
      errors.new_password = 'New password is required';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'New password must be at least 8 characters';
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    return errors;
  };

  const handleTestOrganizationHMRCConnection = async () => {
    setHmrcTestLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.testOrganizationHMRCConnection),
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        let msg;
        if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
          msg = detail.message || data.message || 'HMRC test failed';
        } else if (Array.isArray(detail)) {
          msg = detail.map((d) => d.msg || d).join(', ');
        } else {
          msg = typeof detail === 'string' ? detail : (data.message || 'HMRC test failed');
        }
        throw new Error(msg);
      }
      if (data.hmrc_connection_ok === false) {
        throw new Error(data.result?.error || 'HMRC connection test failed');
      }
      try {
        const freshUserResponse = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl('/unified-auth/me')
        );
        if (freshUserResponse.ok) {
          const freshUserData = await freshUserResponse.json();
          localStorage.setItem('user', JSON.stringify(freshUserData));
          setLocalUser(freshUserData);
          window.dispatchEvent(new Event('user-updated'));
        }
      } catch (refreshErr) {
        console.error('Error refreshing user after HMRC test:', refreshErr);
      }
      if (window.showToast) {
        window.showToast(
          "CIS Transaction Engine test passed: HMRC accepted your Gateway credentials together with this organisation's UTR and Accounts Office reference.",
          'success',
          7000
        );
      }
    } catch (e) {
      if (window.showToast) {
        window.showToast(e.message || 'HMRC test failed', 'error', 6000);
      }
    } finally {
      setHmrcTestLoading(false);
    }
  };

  const handleSaveHmrcGatewayCredentials = async () => {
    setHmrcGatewaySaving(true);
    setError('');
    try {
      const payload = {};
      const uid = (profileData.hmrc_gateway_user_id || '').trim();
      const pw = (profileData.hmrc_gateway_password || '').trim();
      if (uid) payload.hmrc_gateway_user_id = uid;
      if (pw) payload.hmrc_gateway_password = pw;
      if (Object.keys(payload).length === 0) {
        if (window.showToast) {
          window.showToast(
            'Enter a Gateway user ID or password to save. Leave both blank if you are not changing stored credentials.',
            'info',
            5000
          );
        }
        return;
      }

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl('/unified-auth/organization'),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to save Government Gateway credentials';
        if (errorData.detail) {
          errorMessage = Array.isArray(errorData.detail)
            ? errorData.detail.map((err) => err.msg).join(', ')
            : String(errorData.detail);
        }
        setError(errorMessage);
        if (window.showToast) window.showToast(errorMessage, 'error', 5000);
        return;
      }

      await response.json().catch(() => ({}));

      try {
        const orgResp = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl(API_CONFIG.endpoints.getOrganizationProfile)
        );
        if (orgResp.ok) {
          const orgData = await orgResp.json();
          setProfileData((prev) => ({
            ...prev,
            hmrc_gateway_user_id: (orgData.hmrc_gateway_user_id || '').trim(),
            hmrc_gateway_password: (orgData.hmrc_gateway_password || '').trim(),
          }));
        }
      } catch (orgErr) {
        console.error('Error reloading gateway credentials:', orgErr);
      }

      try {
        const freshUserResponse = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl('/unified-auth/me')
        );
        if (freshUserResponse.ok) {
          const freshUserData = await freshUserResponse.json();
          localStorage.setItem('user', JSON.stringify(freshUserData));
          setLocalUser(freshUserData);
          window.dispatchEvent(new Event('user-updated'));
        }
      } catch (refreshErr) {
        console.error('Error refreshing user after gateway save:', refreshErr);
      }

      const msg = 'Government Gateway credentials saved.';
      setSuccess(msg);
      if (window.showToast) window.showToast(msg, 'success', 4000);
    } catch (err) {
      console.error('Error saving gateway credentials:', err);
      setError('Network error. Please try again.');
      if (window.showToast) window.showToast('Network error. Please try again.', 'error', 4000);
    } finally {
      setHmrcGatewaySaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setError('Please enter your password to confirm account deletion');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      // Use direct fetch instead of fetchWithAuth to avoid auto-logout on 401
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(
        API_CONFIG.getApiUrl('/unified-auth/delete-account'),
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            password: deletePassword
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle 401 (wrong password) differently - don't log out
        if (response.status === 401) {
          setError(data.detail || 'Incorrect password. Please try again.');
          setDeleting(false);
          return;
        }
        throw new Error(data.detail || 'Failed to delete account');
      }

      // Account deleted successfully
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_type');
      localStorage.removeItem('user');
      
      // Show success message briefly before redirect
      alert('Your account has been deleted successfully.');
      
      // Force full page reload to reset App.js state and update header
      window.location.href = '/';
      
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (profileSection === 'organization') {
      await saveOrganizationSection('all');
      return;
    }
    
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) {
      if (errors.paye_reference) {
        setPayeReferenceValidationError(errors.paye_reference);
      }
      if (errors.accounts_office_reference) {
        setAccountsOfficeReferenceValidationError(errors.accounts_office_reference);
      }
      const firstMsg = Object.values(errors)[0];
      setError(firstMsg || 'Please fix the validation errors');
      if (window.showToast) {
        window.showToast(firstMsg || 'Please fix the validation errors', 'error', 5000);
      }
      return;
    }

    // Check VAT verification only when saving organization info and VAT applies.
    if (profileSection === 'organization') {
      const employerVatLiable =
        isEmployer() && String(profileData.vat_registered || '').trim() === 'Yes';
      const subShowsVat = !isEmployer();
      if (employerVatLiable || subShowsVat) {
        const currentVatNumber = profileData.vat_number_form || profileData.vat_number || '';
        if (currentVatNumber && currentVatNumber !== originalVatNumber && !vatValidated) {
          setError('Please verify your VAT number by clicking the "Verify VAT" button before saving.');
          if (window.showToast) {
            window.showToast('Please verify your VAT number before saving', 'error', 4000);
          }
          return;
        }
      }
    }

    setSaving(true);
    setError('');

    try {
      // Prepare data for submission - combine forename and surname into full_name
      // Combine address lines into a single address field for backend
      const addressParts = [
        profileData.address_line1,
        profileData.address_line2,
        profileData.address_line3,
        profileData.address_line4,
        profileData.address_line5
      ].filter(part => part && part.trim());

      const isEmployerOrgSettings = isEmployer() && profileSection === 'organization';
      const showSubCisSelfEmployedFields = hasSubcontractorDashboardAccess(localUser);
      let vatNumberForSubmit = profileData.vat_number_form || profileData.vat_number || null;
      let vatRegisteredForSubmit = profileData.vat_registered || null;
      if (isEmployerOrgSettings) {
        if (String(profileData.vat_registered || '').trim() === 'Yes') {
          vatRegisteredForSubmit = 'Yes';
        } else {
          vatRegisteredForSubmit = 'No';
          vatNumberForSubmit = null;
        }
      }
      
      const submitData = {
        full_name: `${profileData.forename} ${profileData.surname}`.trim() || null,
        phone_number: profileData.phone_number || null,
        address: addressParts.length > 0 ? addressParts.join('\n') : null,
        city: profileData.city || null,
        postal_code: profileData.postcode || null,
        trade_type: profileData.trade_type === 'Other (please specify)' 
          ? (profileData.trade_type_other || null)
          : (profileData.trade_type || null),
        building_name_no: profileData.building_name_no ? profileData.building_name_no.trim() : null,
        street: profileData.street ? profileData.street.trim() : null,
        // Explicitly handle empty strings - convert to null so backend clears the field
        unique_tax_reference: profileData.utr && profileData.utr.trim() ? profileData.utr.trim() : null, // Sync with UTR field
        utr: profileData.utr && profileData.utr.trim() ? profileData.utr.trim() : null,
        company_number: profileData.company_number && profileData.company_number.trim() ? profileData.company_number.trim() : null,
        ni_number: profileData.ni_number && profileData.ni_number.trim() ? profileData.ni_number.trim() : null,
        vat_number: vatNumberForSubmit,
        vat_number_form: vatNumberForSubmit,
        vat_registered: vatRegisteredForSubmit,
        // vat_rate removed - using default UK rate (20%)
        // cis_rate removed - now handled by HMRC verification
        // Banking information - Legacy fields (for backward compatibility)
        bank_account_name: profileData.bank_account_name || profileData.account_holder_name || null,
        // Always send bank fields so backend can clear when empty (use each field's own value, not fallback)
        bank_sort_code: (profileData.bank_sort_code ?? profileData.sort_code ?? '').toString().trim() || null,
        bank_account_number: (profileData.bank_account_number ?? profileData.account_number ?? '').toString().trim() || null,
        // Banking information - Preferred fields (matching specification)
        account_holder_name: profileData.account_holder_name || profileData.bank_account_name || null,
        sort_code: (profileData.sort_code ?? profileData.bank_sort_code ?? '').toString().trim() || null,
        account_number: (profileData.account_number ?? profileData.bank_account_number ?? '').toString().trim() || null,
        verified: profileData.verified !== undefined ? profileData.verified : false,
        // Business/Company fields (merged from employer and professional)
        business_type: profileData.business_type || null,
        ...(showSubCisSelfEmployedFields
          ? {
              cis_liable: profileData.cis_liable || null,
              self_employed: profileData.self_employed || null,
            }
          : {}),
        registered_name: profileData.registered_name || null,
        company_type: profileData.company_type || null,
        accounts_office_reference:
          profileData.accounts_office_reference &&
          typeof profileData.accounts_office_reference === 'string' &&
          profileData.accounts_office_reference.trim()
            ? profileData.accounts_office_reference.trim().toUpperCase().replace(/\s+/g, '')
            : null,
        // Always include paye_reference for employers, even if empty (will be null)
        ...(isEmployer() ? {
          paye_reference: profileData.paye_reference && typeof profileData.paye_reference === 'string' && profileData.paye_reference.trim() 
            ? profileData.paye_reference.trim() 
            : null
        } : {})
      };

      // Separate payloads/endpoints by tab.
      const userSubmitData = {
        full_name: submitData.full_name,
        phone_number: submitData.phone_number,
      };
      const orgSubmitData = {
        business_type: submitData.business_type,
        organization_name: submitData.registered_name,
        company_type: submitData.company_type,
        company_number: submitData.company_number,
        utr: submitData.utr,
        accounts_office_reference: submitData.accounts_office_reference,
        paye_reference: submitData.paye_reference,
        building_name_no: submitData.building_name_no,
        street: submitData.street,
        city: submitData.city,
        postal_code: submitData.postal_code,
        address: submitData.address,
        trade_type: submitData.trade_type,
        ...(showSubCisSelfEmployedFields
          ? {
              cis_liable: submitData.cis_liable,
              self_employed: submitData.self_employed,
            }
          : {}),
        ni_number: submitData.ni_number,
        unique_tax_reference: submitData.unique_tax_reference,
        vat_number: submitData.vat_number,
        vat_number_form: submitData.vat_number_form,
        vat_registered: submitData.vat_registered,
        bank_account_name: submitData.bank_account_name,
        bank_sort_code: submitData.bank_sort_code,
        bank_account_number: submitData.bank_account_number,
        account_holder_name: submitData.account_holder_name,
        sort_code: submitData.sort_code,
        account_number: submitData.account_number,
      };

      if (isEmployer() && profileSection === 'organization') {
        orgSubmitData.contract_terms =
          typeof profileData.contract_terms === 'string'
            ? profileData.contract_terms.trim() || null
            : null;
      }

      // Don't remove null values - we need to send them to clear fields in the database
      // The backend will handle null values properly to clear fields

      // Save HMRC auto-settings (for employers, organization tab only)
      if (isEmployer() && profileSection === 'organization') {
        try {
          const settingsResponse = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl('/unified-auth/hmrc/save-settings'),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                auto_verify_cis: profileData.auto_verify_cis !== undefined ? profileData.auto_verify_cis : true,
                auto_submit_returns: profileData.auto_submit_returns !== undefined ? profileData.auto_submit_returns : false
              })
            }
          );

          if (!settingsResponse.ok) {
            const settingsError = await settingsResponse.json();
            console.error('Error saving HMRC auto-settings:', settingsError);
            // Don't fail the whole operation, just log the error
          } else {
            const settingsResult = await settingsResponse.json();
            console.log('HMRC settings saved successfully:', settingsResult);
          }
        } catch (settingsErr) {
          console.error('Error saving HMRC auto-settings:', settingsErr);
          // Don't fail the whole operation
        }
      }

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(profileSection === 'personal' ? '/unified-auth/profile' : '/unified-auth/organization'),
        {
          method: profileSection === 'personal' ? 'PUT' : 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(profileSection === 'personal' ? userSubmitData : orgSubmitData)
        }
      );

      if (response.ok) {
        await response.json().catch(() => ({}));
        
        // After saving everything, fetch fresh user data to ensure HMRC settings are loaded
        let latestUserData = localUser || {};
        try {
          const freshUserResponse = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl('/unified-auth/me')
          );
          if (freshUserResponse.ok) {
            const freshUserData = await freshUserResponse.json();
            latestUserData = freshUserData;
            localStorage.setItem('user', JSON.stringify(freshUserData));
            setLocalUser(freshUserData);
            window.dispatchEvent(new Event('user-updated'));
            setProfileData(prev => ({
              ...prev,
              auto_verify_cis: freshUserData.auto_verify_cis !== undefined ? freshUserData.auto_verify_cis : true,
              auto_submit_returns: freshUserData.auto_submit_returns !== undefined ? freshUserData.auto_submit_returns : false,
            }));
          }
        } catch (refreshErr) {
          console.error('Error refreshing user data:', refreshErr);
          // Don't fail the operation
        }
        
        const hadBankDetails = (profileData.sort_code || profileData.bank_sort_code || profileData.account_number || profileData.bank_account_number) && (profileData.sort_code || profileData.bank_sort_code) && (profileData.account_number || profileData.bank_account_number);
        const bankValidated = latestUserData.bank_validation_status === 'valid';
        const successMsg = hadBankDetails && bankValidated
          ? 'Profile updated. Bank details validated successfully.'
          : `${profileSection === 'personal' ? 'User' : 'Organization'} info updated successfully!`;
        setSuccess(successMsg);
        setPayeReferenceValidationError('');
        if (window.showToast) {
          window.showToast(successMsg, 'success', 4000);
        }
      } else {
        const errorData = await response.json();
        let errorMessage = `Failed to update ${profileSection === 'personal' ? 'user' : 'organization'} info`;
        
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => err.msg).join(', ');
          } else {
            errorMessage = errorData.detail;
          }
        }
        
        setError(errorMessage);
        if (window.showToast) {
          window.showToast(errorMessage, 'error', 4000);
        }
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Network error. Please try again.');
      if (window.showToast) {
        window.showToast('Network error. Please try again.', 'error', 4000);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDashboardRoleSwitch = async (newRole) => {
    if (!localUser || dashboardRoleSwitching) return;
    const roles = normalizeAvailableRoles(localUser);
    if (!roles.includes(newRole)) return;
    const cur = String(localUser.current_role || '').toLowerCase();
    if (newRole === 'contractor' && (cur === 'contractor' || cur === 'both')) return;
    if (newRole === 'subcontractor' && cur === 'subcontractor') return;

    setDashboardRoleSwitching(true);
    setError('');
    try {
      const resp = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.switchRole),
        {
          method: 'POST',
          body: JSON.stringify({ new_role: newRole }),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = data?.detail;
        throw new Error(Array.isArray(msg) ? msg.map((x) => x.msg || x).join(', ') : msg || 'Could not switch dashboard');
      }
      const fresh = data.user;
      if (fresh) {
        localStorage.setItem('user', JSON.stringify(fresh));
        setLocalUser(fresh);
        window.dispatchEvent(new Event('user-updated'));
      }
      if (window.showToast) {
        window.showToast(data.message || 'Dashboard view updated.', 'success', 3000);
      }
    } catch (err) {
      const message = err.message || 'Could not switch dashboard';
      setError(message);
      if (window.showToast) window.showToast(message, 'error', 4000);
    } finally {
      setDashboardRoleSwitching(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      if (window.showToast) {
        window.showToast('Please fix the password validation errors', 'error', 4000);
      }
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(API_CONFIG.getApiUrl('/subcontractors/change-password'), {
        method: 'POST',
        headers: API_CONFIG.getAuthHeaders(),
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setShowPasswordForm(false);
        if (window.showToast) {
          window.showToast('Password changed successfully!', 'success', 4000);
        }
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to change password';
        
        if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        
        if (window.showToast) {
          window.showToast(errorMessage, 'error', 4000);
        }
      }
    } catch (err) {
      console.error('Error changing password:', err);
      if (window.showToast) {
        window.showToast('Network error. Please try again.', 'error', 4000);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="subcontractor-profile-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subcontractor-profile-container">
      <div className="profile-header">
        <h1>
          {profileSection === 'organization'
            ? 'Organisation settings'
            : profileSection === 'users'
              ? 'Users'
              : 'My Profile'}
        </h1>
        <p>
          {profileSection === 'organization'
            ? 'Manage your organisation details, banking, compliance and contract terms.'
            : profileSection === 'users'
              ? 'Invite and manage users in your organisation.'
              : 'Manage your personal information and security.'}
        </p>
        {localUser?.company && profileSection === 'personal' && (
          <p className="company-info">Company: <strong>{localUser.company.company_name}</strong></p>
        )}
      </div>

      <div className="profile-content">
        {localUser && showEmployerSubcontractorDashboardSwitch(localUser) ? (
          <div className="profile-dashboard-role-switch">
            <h2 className="profile-dashboard-role-switch__title">Dashboard view</h2>
            <p className="profile-dashboard-role-switch__hint">
              You are linked to at least one employer (including any pending connection) and can also use the employer
              dashboard. Choose which view you are working in — this updates the main navigation and dashboard context.
            </p>
            <div
              className="profile-dashboard-role-switch__toggle"
              role="group"
              aria-label="Switch between employer and subcontractor dashboard"
            >
              <button
                type="button"
                className={`profile-dashboard-role-switch__btn${isEmployerDashboardRoleActive(localUser) ? ' profile-dashboard-role-switch__btn--active' : ''}`}
                disabled={dashboardRoleSwitching}
                onClick={() => handleDashboardRoleSwitch('contractor')}
              >
                Employer
              </button>
              <button
                type="button"
                className={`profile-dashboard-role-switch__btn${!isEmployerDashboardRoleActive(localUser) ? ' profile-dashboard-role-switch__btn--active' : ''}`}
                disabled={dashboardRoleSwitching}
                onClick={() => handleDashboardRoleSwitch('subcontractor')}
              >
                Subcontractor
              </button>
            </div>
            {dashboardRoleSwitching ? (
              <p className="profile-dashboard-role-switch__status">Updating…</p>
            ) : null}
          </div>
        ) : null}
        {profileSection === 'users' && canViewUsersTab ? (
          <ProfileUsersTab
            currentUser={localUser}
            canAddUsers={canAddUsers}
            canDeleteUsers={canDeleteUsers}
          />
        ) : null}
        {profileSection !== 'users' && (
        <form id="subcontractor-profile-form" noValidate onSubmit={handleSaveProfile} className="profile-form">
          {/* Personal Information Section */}
          {profileSection === 'personal' && (
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Forename</label>
                <input
                  type="text"
                  name="forename"
                  value={profileData.forename}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Surname</label>
                <input
                  type="text"
                  name="surname"
                  value={profileData.surname}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleInputChange}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                  Email cannot be changed
                </small>
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={profileData.phone_number}
                  onChange={handleInputChange}
                />
              </div>
            </div>

          </div>
          )}

          {/* Professional Information Section - Different fields based on business type */}
          {profileSection === 'organization' && (
          <>
          <div className="form-section org-details-section">
            <h3>Organisation Details</h3>
            <div className="org-details-fields">
            {profileData.business_type !== 'Personal (Sole Trader)' && (
              <div className="form-group">
                <label htmlFor="registered_name_org">Registered Name</label>
                <input
                  type="text"
                  id="registered_name_org"
                  name="registered_name"
                  value={profileData.registered_name}
                  onChange={handleInputChange}
                  placeholder="Registered company name"
                />
              </div>
            )}

            <div className="form-group">
              <label>Company Type</label>
              <select
                value={
                  profileData.business_type === 'Personal (Sole Trader)'
                    ? 'SOLE_TRADER'
                    : 'LTD'
                }
                onChange={(e) => {
                  const selected = e.target.value;
                  setProfileData((prev) => ({
                    ...prev,
                    business_type:
                      selected === 'SOLE_TRADER'
                        ? 'Personal (Sole Trader)'
                        : 'Business (Ltd co or Partnership)',
                    company_type: selected === 'SOLE_TRADER' ? '' : 'LTD',
                    registered_name: selected === 'SOLE_TRADER' ? '' : prev.registered_name,
                    company_number: selected === 'SOLE_TRADER' ? '' : prev.company_number,
                  }));
                }}
                className="form-input"
                style={{ width: '100%', padding: '10px 15px', color: '#333', backgroundColor: '#fff' }}
              >
                <option value="LTD">Ltd company</option>
                <option value="SOLE_TRADER">Sole trader</option>
              </select>
            </div>

            {/* Trade */}
            <div className="form-group">
              <label>Trade</label>
              <select
                name="trade_type"
                value={profileData.trade_type}
                onChange={handleInputChange}
                className="form-input"
                style={{ width: '100%', padding: '10px 15px', color: '#333', backgroundColor: '#fff' }}
              >
                <option value="">Please select...</option>
                <option value="Architect / Designer / Surveyor (consultancy only)">Architect / Designer / Surveyor (consultancy only)</option>
                <option value="Asbestos remover">Asbestos remover</option>
                <option value="Bathroom fitter">Bathroom fitter</option>
                <option value="Brick / Block layer">Brick / Block layer</option>
                <option value="Canteen operator / Welfare provider">Canteen operator / Welfare provider</option>
                <option value="Carpenter / Joiner">Carpenter / Joiner</option>
                <option value="Civil engineer (on-site works)">Civil engineer (on-site works)</option>
                <option value="Cladder / Curtain wall installer">Cladder / Curtain wall installer</option>
                <option value="Cleaner (construction / builders' clean)">Cleaner (construction / builders' clean)</option>
                <option value="Cleaner (domestic / maintenance)">Cleaner (domestic / maintenance)</option>
                <option value="Concrete worker / Paver / Kerber">Concrete worker / Paver / Kerber</option>
                <option value="Crane hire (without labour)">Crane hire (without labour)</option>
                <option value="Crane operator (with labour)">Crane operator (with labour)</option>
                <option value="Delivery driver / Haulage">Delivery driver / Haulage</option>
                <option value="Demolition worker">Demolition worker</option>
                <option value="Drainage / Pipework installer (external)">Drainage / Pipework installer (external)</option>
                <option value="Electrician">Electrician</option>
                <option value="Engineer (consulting only)">Engineer (consulting only)</option>
                <option value="Fire alarm / Security system installer">Fire alarm / Security system installer</option>
                <option value="Floor fitter / Carpet layer">Floor fitter / Carpet layer</option>
                <option value="General contractor / Builder">General contractor / Builder</option>
                <option value="Ground investigation / Borehole driller">Ground investigation / Borehole driller</option>
                <option value="Groundworker / Excavator / Digger driver">Groundworker / Excavator / Digger driver</option>
                <option value="HVAC installer / Ventilation engineer">HVAC installer / Ventilation engineer</option>
                <option value="Insulation installer">Insulation installer</option>
                <option value="Kitchen fitter">Kitchen fitter</option>
                <option value="Landscaper (hard landscaping)">Landscaper (hard landscaping)</option>
                <option value="Landscaper (soft landscaping only)">Landscaper (soft landscaping only)</option>
                <option value="Leadworker">Leadworker</option>
                <option value="Lift / Elevator installer">Lift / Elevator installer</option>
                <option value="Painter / Decorator">Painter / Decorator</option>
                <option value="Pest control / Environmental cleaning">Pest control / Environmental cleaning</option>
                <option value="Plant hire (without operator)">Plant hire (without operator)</option>
                <option value="Plant operator (with labour)">Plant operator (with labour)</option>
                <option value="Plasterer / Plasterboard fixer / Dry liner / Taper & jointer">Plasterer / Plasterboard fixer / Dry liner / Taper & jointer</option>
                <option value="Plumber / Heating engineer">Plumber / Heating engineer</option>
                <option value="Roofer">Roofer</option>
                <option value="Scaffold hire (no labour)">Scaffold hire (no labour)</option>
                <option value="Scaffolder (with labour)">Scaffolder (with labour)</option>
                <option value="Sign fitter / Fascia installer">Sign fitter / Fascia installer</option>
                <option value="Site Manager / Foreman (self-employed)">Site Manager / Foreman (self-employed)</option>
                <option value="Site security / Watchman">Site security / Watchman</option>
                <option value="Steel fixer / Fabricator / Welder (on site)">Steel fixer / Fabricator / Welder (on site)</option>
                <option value="Supplier of materials only">Supplier of materials only</option>
                <option value="Telecoms / Data installer">Telecoms / Data installer</option>
                <option value="Temporary works / Site hoarding erector">Temporary works / Site hoarding erector</option>
                <option value="Tiler (wall / floor / roof)">Tiler (wall / floor / roof)</option>
                <option value="Tree surgeon / Arborist">Tree surgeon / Arborist</option>
                <option value="Waste removal (with labour / site clearance)">Waste removal (with labour / site clearance)</option>
                <option value="Waste removal / Skip hire (no labour)">Waste removal / Skip hire (no labour)</option>
                <option value="Window fitter / Glazier">Window fitter / Glazier</option>
                <option value="Other (please specify)">Other (please specify)</option>
              </select>
              {profileData.trade_type === 'Other (please specify)' && (
                <div className="form-group" style={{ marginTop: '15px' }}>
                  <label>Please specify your trade type *</label>
                  <input
                    type="text"
                    name="trade_type_other"
                    value={profileData.trade_type_other}
                    onChange={handleInputChange}
                    placeholder="Enter your trade type"
                    className="form-input"
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="address_line1"
                value={profileData.address_line1}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="address_line2"
                value={profileData.address_line2}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Address Line 3</label>
              <input
                type="text"
                name="address_line3"
                value={profileData.address_line3}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 4</label>
              <input
                type="text"
                name="address_line4"
                value={profileData.address_line4}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Address Line 5</label>
              <input
                type="text"
                name="address_line5"
                value={profileData.address_line5}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>City/County</label>
              <input
                type="text"
                name="city"
                value={profileData.city}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Postcode</label>
              <input
                type="text"
                name="postcode"
                value={profileData.postcode}
                onChange={handleInputChange}
              />
            </div>
            </div>
            <div className="form-actions" style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="save-btn"
                onClick={() => saveOrganizationSection('organization_details')}
                disabled={saving || hmrcTestLoading || hmrcGatewaySaving || hmrcAutoSettingsSaving}
              >
                {saving ? 'Saving...' : 'Save Organisation Details'}
              </button>
            </div>
          </div>
          <div className="form-section">
            <h3>Tax Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tax_info_utr">UTR</label>
                <input
                  type="text"
                  id="tax_info_utr"
                  name="utr"
                  value={profileData.utr}
                  onChange={handleInputChange}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  placeholder="10 digit UTR"
                  style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                />
                {utrValidationError && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '6px' }}>
                    {utrValidationError}
                  </small>
                )}
              </div>
              {isEmployer() ? (
                <div className="form-group">
                  <label htmlFor="tax_info_paye_reference">PAYE Reference</label>
                  <input
                    type="text"
                    id="tax_info_paye_reference"
                    name="paye_reference"
                    value={profileData.paye_reference || ''}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const v = (e.target.value || '').trim();
                      if (!v) {
                        setPayeReferenceValidationError('');
                        return;
                      }
                      setPayeReferenceValidationError(
                        isValidPayeReference(v) ? '' : PAYE_REFERENCE_INVALID_MESSAGE
                      );
                    }}
                    maxLength={PAYE_REFERENCE_MAX_LENGTH}
                    placeholder="e.g. 914/VA11166"
                    autoComplete="off"
                    style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                  />
                  {payeReferenceValidationError && (
                    <small style={{ color: '#dc3545', display: 'block', marginTop: '6px' }}>
                      {payeReferenceValidationError}
                    </small>
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="tax_info_ni_number">NI Number</label>
                  <input
                    type="text"
                    id="tax_info_ni_number"
                    name="ni_number"
                    value={profileData.ni_number}
                    onChange={handleInputChange}
                    placeholder="AB123456C"
                    maxLength="9"
                  />
                  {niValidationError && (
                    <small style={{ color: '#dc3545', display: 'block', marginTop: '6px' }}>
                      {niValidationError}
                    </small>
                  )}
                </div>
              )}
            </div>

            <div className="form-row">
              {isEmployer() ? (
                <div className="form-group">
                  <label htmlFor="tax_info_accounts_office_reference">Accounts Office Reference</label>
                  <input
                    type="text"
                    id="tax_info_accounts_office_reference"
                    name="accounts_office_reference"
                    value={profileData.accounts_office_reference || ''}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const v = (e.target.value || '').trim().toUpperCase().replace(/\s+/g, '');
                      if (!v) {
                        setAccountsOfficeReferenceValidationError('');
                        return;
                      }
                      setAccountsOfficeReferenceValidationError(
                        isValidAccountsOfficeReference(v)
                          ? ''
                          : ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE
                      );
                    }}
                    maxLength={ACCOUNTS_OFFICE_REFERENCE_MAX_LENGTH}
                    placeholder="e.g. 914PV00196767"
                    autoComplete="off"
                    style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                  />
                  {accountsOfficeReferenceValidationError && (
                    <small style={{ color: '#dc3545', display: 'block', marginTop: '6px' }}>
                      {accountsOfficeReferenceValidationError}
                    </small>
                  )}
                </div>
              ) : (
                <div className="form-group"></div>
              )}

              <div className="form-group">
                <label>CIS Liable</label>
                <select
                  name="cis_liable"
                  value={profileData.cis_liable}
                  onChange={handleInputChange}
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              {isEmployer() ? (
                <>
                  <div className="form-group">
                    <label htmlFor="employer_vat_liable">VAT liable</label>
                    <select
                      id="employer_vat_liable"
                      name="vat_registered"
                      value={profileData.vat_registered === 'Yes' ? 'Yes' : 'No'}
                      onChange={handleInputChange}
                      className="form-input"
                      style={{ width: '100%', padding: '10px 15px', color: '#333', backgroundColor: '#fff' }}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                    <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '6px' }}>
                      If you are not VAT registered, choose No — the VAT number field will be hidden.
                    </small>
                  </div>
                  <div className="form-group" />
                </>
              ) : (
                <div className="form-group">
                  <label>Self employed</label>
                  <select
                    name="self_employed"
                    value={profileData.self_employed}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              )}
            </div>

            {/* Gateway Credentials Section - Removed: Now stored in ENV variables (system-wide) */}

            {hasSubcontractorDashboardAccess(localUser) && isEmployer() && (
              <div className="form-row">
                <div className="form-group">
                  <label>Self employed</label>
                  <select
                    name="self_employed"
                    value={profileData.self_employed}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="form-group"></div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group" style={{ width: '100%' }}>
                {(!isEmployer() || profileData.vat_registered === 'Yes') ? (
                  <>
                    <label htmlFor="vat_number_form">VAT Number (Optional)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        id="vat_number_form"
                        name="vat_number_form"
                        value={profileData.vat_number_form}
                        onChange={handleInputChange}
                        placeholder="Enter VAT number (e.g., GB999999999)"
                        style={{
                          flex: 1,
                          fontFamily: 'monospace',
                          letterSpacing: '1px'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => validateVAT(profileData.vat_number_form)}
                        disabled={vatValidating || !profileData.vat_number_form || profileData.vat_number_form.trim() === ''}
                        style={{
                          padding: '13px 16px',
                          backgroundColor: vatValidated ? '#28a745' : '#57bedd',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (vatValidating || !profileData.vat_number_form || profileData.vat_number_form.trim() === '') ? 'not-allowed' : 'pointer',
                          opacity: (vatValidating || !profileData.vat_number_form || profileData.vat_number_form.trim() === '') ? 0.6 : 1,
                          whiteSpace: 'nowrap',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        {vatValidating ? 'Verifying...' : vatValidated ? '✓ Verified' : 'Verify VAT'}
                      </button>
                    </div>
                    {vatValidating && (
                      <div style={{ fontSize: '12px', color: '#57bedd', marginTop: '4px' }}>
                        Validating VAT number with HMRC...
                      </div>
                    )}
                    {vatValidationSuccess && !vatValidating && (
                      <div style={{ fontSize: '12px', color: '#28a745', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>✓</span> VAT number verified successfully with HMRC
                      </div>
                    )}
                    {vatValidationError && !vatValidating && (
                      <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '4px' }}>
                        {vatValidationError}
                      </div>
                    )}
                    {profileData.vat_number_form && profileData.vat_number_form !== originalVatNumber && !vatValidated && !vatValidating && (
                      <div style={{ fontSize: '12px', color: '#ffc107', marginTop: '4px' }}>
                        ⚠ Please verify your VAT number before saving
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ minHeight: '1px' }} />
                )}
              </div>
            </div>

            {hasSubcontractorOrgSettingsAccess() && profileData.vat_registered === 'Yes' && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label htmlFor="subcontractor_reverse_charge_default">
                    Reverse charge usually applies to my invoices
                  </label>
                  <select
                    id="subcontractor_reverse_charge_default"
                    name="subcontractor_reverse_charge_default"
                    value={profileData.subcontractor_reverse_charge_default ? 'yes' : 'no'}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        subcontractor_reverse_charge_default: e.target.value === 'yes',
                      }))
                    }
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  <small style={{ color: 'rgba(69, 69, 69, 0.85)', display: 'block', marginTop: '6px' }}>
                    Advisory only — helps prefill future payment requests. Your contractor confirms the final VAT treatment.
                  </small>
                </div>
              </div>
            )}

            {isEmployer() && profileData.vat_registered === 'Yes' && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label htmlFor="employer_reverse_charge_default">
                    We usually apply Domestic Reverse Charge VAT
                  </label>
                  <select
                    id="employer_reverse_charge_default"
                    name="employer_reverse_charge_default"
                    value={profileData.employer_reverse_charge_default ? 'yes' : 'no'}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        employer_reverse_charge_default: e.target.value === 'yes',
                      }))
                    }
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                  <small style={{ color: 'rgba(69, 69, 69, 0.85)', display: 'block', marginTop: '6px' }}>
                    Sets the default on payment approvals. You can still override for each payment.
                  </small>
                </div>
              </div>
            )}

            {/* HMRC Auto-Settings - Only for Employers */}
            {isEmployer() && (
              <div className="form-section" style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid rgba(255, 255, 255, 0.2)' }}>
                <h3 style={{ color: 'rgba(69, 69, 69, 0.85)', marginBottom: '10px', fontSize: '18px', fontWeight: '600' }}>HMRC Automation Settings</h3>
                <p style={{ color: 'rgba(69, 69, 69, 0.85)', fontSize: '14px', marginBottom: '25px', lineHeight: '1.5' }}>
                  Configure automatic CIS verification and submission preferences.
                </p>
                {hmrcAutoSettingsSaving && (
                  <p style={{ color: '#57bedd', fontSize: '12px', marginBottom: '12px' }}>
                    Saving automation settings...
                  </p>
                )}
                
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    padding: '15px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                      <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '600' }}>
                        Automatically verify CIS subcontractors
                      </span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: '1.4' }}>
                        When enabled, new subcontractors will be automatically verified with HMRC before first payment
                      </span>
                    </div>
                    <div style={{ position: 'relative', width: '48px', height: '24px' }}>
                      <input
                        type="checkbox"
                        name="auto_verify_cis"
                        checked={profileData.auto_verify_cis !== undefined ? profileData.auto_verify_cis : true}
                        onChange={handleInputChange}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          zIndex: 2
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: (profileData.auto_verify_cis !== undefined ? profileData.auto_verify_cis : true) ? '#4caf50' : 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '12px',
                        transition: 'background 0.3s',
                        pointerEvents: 'none'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '""',
                          height: '18px',
                          width: '18px',
                          left: (profileData.auto_verify_cis !== undefined ? profileData.auto_verify_cis : true) ? '26px' : '3px',
                          bottom: '3px',
                          background: 'white',
                          borderRadius: '50%',
                          transition: 'left 0.3s',
                        }}></span>
                      </span>
                    </div>
                  </label>
                </div>

                <div className="form-group">
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    padding: '15px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.4)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)'}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                      <span style={{ color: '#ffffff', fontSize: '15px', fontWeight: '600' }}>
                        Automatically submit CIS returns
                      </span>
                      <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: '1.4' }}>
                        When enabled, CIS300 monthly returns will be automatically submitted to HMRC at the end of each CIS month
                      </span>
                    </div>
                    <div style={{ position: 'relative', width: '48px', height: '24px' }}>
                      <input
                        type="checkbox"
                        name="auto_submit_returns"
                        checked={profileData.auto_submit_returns || false}
                        onChange={handleInputChange}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer',
                          zIndex: 2
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: profileData.auto_submit_returns ? '#4caf50' : 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '12px',
                        transition: 'background 0.3s',
                        pointerEvents: 'none'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '""',
                          height: '18px',
                          width: '18px',
                          left: profileData.auto_submit_returns ? '26px' : '3px',
                          bottom: '3px',
                          background: 'white',
                          borderRadius: '50%',
                          transition: 'left 0.3s',
                        }}></span>
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <div className="form-actions" style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="save-btn"
                onClick={() => saveOrganizationSection('tax_information')}
                disabled={saving || hmrcTestLoading || hmrcGatewaySaving || hmrcAutoSettingsSaving}
              >
                {saving ? 'Saving...' : 'Save Tax Information'}
              </button>
            </div>

          </div>
          </>
          )}

          {/* Banking Details Section */}
          {profileSection === 'organization' && (
          <div className="form-section">
            <h3>Banking Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={profileData.account_holder_name || ''}
                  onChange={handleInputChange}
                  placeholder="Name on account"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sort Code</label>
                <input
                  type="text"
                  name="sort_code"
                  value={profileData.sort_code || ''}
                  onChange={handleInputChange}
                  maxLength="6"
                  placeholder="123456"
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}
                />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="account_number"
                  value={profileData.account_number || ''}
                  onChange={handleInputChange}
                  maxLength="8"
                  placeholder="12345678"
                  style={{
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="verified"
                    checked={profileData.verified || false}
                    onChange={(e) => handleInputChange({ target: { name: 'verified', type: 'checkbox', checked: e.target.checked } })}
                    disabled
                    style={{ width: '18px', height: '18px', cursor: 'not-allowed', opacity: 0.6 }}
                  />
                  <span>Bank Account Verified</span>
                </label>
                <small style={{ fontSize: '12px', color: '#666', display: 'block', marginTop: '4px' }}>
                  Verification status (managed by system)
                </small>
              </div>
            </div>

            <div className="banking-note">
              <p><strong>Note:</strong> Your banking details are encrypted and stored securely. They are only used for payment processing.</p>
            </div>
            <div className="form-actions" style={{ marginTop: '16px' }}>
              <button
                type="button"
                className="save-btn"
                onClick={() => saveOrganizationSection('banking_details')}
                disabled={saving || hmrcTestLoading || hmrcGatewaySaving || hmrcAutoSettingsSaving}
              >
                {saving ? 'Saving...' : 'Save Banking Details'}
              </button>
            </div>
          </div>
          )}

          {profileSection === 'organization' && isEmployer() && (
            <div className="form-section">
              <h3>Subcontractor contract terms &amp; mode</h3>
              <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.5, margin: '0 0 14px' }}>
                Standard subcontractor terms are saved on your organisation when it is created. Subcontractors see
                this text when they connect or accept an invitation, and in the review flow on their dashboard. Edit it
                here whenever you need to reflect your own wording.
              </p>
              <div className="form-group">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                  }}
                >
                  <label htmlFor="contract_terms" style={{ margin: 0 }}>
                    Contract Terms
                  </label>
                </div>
                <textarea
                  id="contract_terms"
                  name="contract_terms"
                  value={profileData.contract_terms || ''}
                  onChange={handleInputChange}
                  rows={16}
                  placeholder="Enter the contract terms subcontractors must accept before working with you."
                  style={{
                    width: '100%',
                    minHeight: '280px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    lineHeight: 1.45,
                    padding: '12px',
                    borderRadius: '6px',
                  }}
                />
              </div>
              <div className="form-actions" style={{ marginTop: '16px', paddingBottom: '20px' }}>
                <button
                  type="button"
                  className="save-btn"
                  onClick={() => saveOrganizationSection('contract_terms')}
                  disabled={saving || hmrcTestLoading || hmrcGatewaySaving || hmrcAutoSettingsSaving}
                >
                  {saving ? 'Saving...' : 'Save Contract Terms'}
                </button>
              </div>
            </div>
          )}

          {/* Security Section */}
          {profileSection === 'personal' && (
          <div className="form-section">
            <h3>Security Settings</h3>
            
            <div className="security-section">
              <h4>Change Password</h4>
              <p>Update your password to keep your account secure.</p>
              
              {!showPasswordForm ? (
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(true)}
                  className="change-password-btn"
                >
                  Change Password
                </button>
              ) : (
                <form onSubmit={handleChangePassword} className="password-form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      required
                      minLength="8"
                    />
                  </div>

                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      required
                      minLength="8"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({
                          current_password: '',
                          new_password: '',
                          confirm_password: ''
                        });
                      }}
                      className="cancel-btn"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="save-btn"
                      disabled={saving}
                    >
                      {saving ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* <div className="security-info">
              <h4>Account Security</h4>
              <div className="security-item">
                <span className="security-label">Email Verified:</span>
                <span className={`security-status ${user?.email_verified ? 'verified' : 'unverified'}`}>
                  {user?.email_verified ? '✅ Verified' : '❌ Not Verified'}
                </span>
              </div>
              <div className="security-item">
                <span className="security-label">Account Status:</span>
                <span className={`security-status ${user?.subcontractor_info?.status === 'active' ? 'active' : 'inactive'}`}>
                  {user?.subcontractor_info?.status === 'active' ? '✅ Active' : '❌ Inactive'}
                </span>
              </div>
            </div> */}
          </div>
          )}

          {/* Form Actions */}
          {profileSection === 'personal' && (
            <div className="form-actions">
              <button
                type="submit"
                className="save-btn"
                disabled={saving || hmrcTestLoading || hmrcGatewaySaving || hmrcAutoSettingsSaving}
              >
                {saving ? 'Saving...' : 'Save User Info'}
              </button>
            </div>
          )}
        </form>
        )}

        {profileSection === 'organization' && isEmployer() && (
          <div className="form-section" style={{ marginTop: '24px', backgroundColor: '#fff', padding: '20px', borderRadius: '10px' }}>
            <h3>HMRC Government Gateway</h3>
            <p style={{ fontSize: '12px', color: '#666', margin: '0 0 12px', lineHeight: 1.45 }}>
              Stored encrypted on your organisation. Saved credentials load here so you can review or replace them.
              Use <strong>Save Gateway credentials</strong> below to store changes—this is separate from the section save buttons above.
              The connection test uses your saved UTR, PAYE reference, and Accounts Office reference; save organisation details first if you changed them.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="hmrc_gateway_user_id">Gateway user ID</label>
                <input
                  type="text"
                  id="hmrc_gateway_user_id"
                  name="hmrc_gateway_user_id"
                  value={profileData.hmrc_gateway_user_id}
                  onChange={handleInputChange}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={hmrcTestLoading || hmrcGatewaySaving || saving}
                  placeholder={
                    localUser?.organization?.hmrc_gateway_user_id_present
                      ? 'On file — edit to replace'
                      : 'e.g. 12-digit Gateway user ID'
                  }
                />
              </div>
              <div className="form-group">
                <label htmlFor="hmrc_gateway_password">Gateway password</label>
                <div className="hmrc-gateway-password-field">
                  <input
                    type={showHmrcGatewayPassword ? 'text' : 'password'}
                    id="hmrc_gateway_password"
                    name="hmrc_gateway_password"
                    value={profileData.hmrc_gateway_password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    disabled={hmrcTestLoading || hmrcGatewaySaving || saving}
                    placeholder={
                      localUser?.organization?.hmrc_gateway_password_present
                        ? 'On file — edit to replace'
                        : 'Enter when setting or changing password'
                    }
                  />
                  <button
                    type="button"
                    className="hmrc-gateway-password-toggle"
                    onClick={() => setShowHmrcGatewayPassword((v) => !v)}
                    disabled={hmrcTestLoading || hmrcGatewaySaving || saving}
                    aria-label={showHmrcGatewayPassword ? 'Hide password' : 'Show password'}
                  >
                    {showHmrcGatewayPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <small style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '12px' }}>
              {localUser?.organization?.hmrc_gateway_user_id_present &&
              localUser?.organization?.hmrc_gateway_password_present
                ? 'Both gateway user ID and password are on file.'
                : 'One or both credentials are not stored yet. Enter them to use CIS Transaction Engine features.'}
            </small>
            <div className="hmrc-gateway-actions">
              <button
                type="button"
                className="change-password-btn"
                onClick={handleTestOrganizationHMRCConnection}
                disabled={hmrcTestLoading || hmrcGatewaySaving || saving}
              >
                {hmrcTestLoading ? 'Testing connection...' : 'Test connection'}
              </button>
              <button
                type="button"
                className="save-btn"
                onClick={handleSaveHmrcGatewayCredentials}
                disabled={hmrcGatewaySaving || hmrcTestLoading || saving}
              >
                {hmrcGatewaySaving ? 'Saving...' : 'Save Gateway credentials'}
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#666', margin: '12px 0 0', lineHeight: 1.45 }}>
              Save Gateway credentials before testing if you have just entered or changed them.
            </p>
          </div>
        )}

        {profileSection === 'personal' && (
        <div className="delete-account-section">
          <h3>Danger Zone</h3>
          <div className="delete-account-warning">
            <p><strong>Delete My Account</strong></p>
            <p>Once you delete your account, there is no going back. This will permanently delete:</p>
            <ul>
              <li>Your user information</li>
              <li>All payment requests (including paid ones)</li>
              <li>Your employer/subcontractor information</li>
              <li>All relationships and connections</li>
              {localUser && (localUser.current_role === 'contractor' || (localUser.available_roles && localUser.available_roles.includes('contractor'))) && (
                <>
                  <li>All subcontractor relationships</li>
                  <li>All HMRC CIS submissions</li>
                  <li>All sub-users under your account</li>
                </>
              )}
            </ul>
            <button
              type="button"
              className="delete-account-btn"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete My Account
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div 
            style={{
              background: '#ffffff',
              borderRadius: '15px',
              maxWidth: '400px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '30px 30px 20px 30px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h2 style={{
                color: '#dc3545',
                fontSize: '24px',
                fontWeight: 700,
                margin: 0,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}>
                Delete Account
              </h2>
            </div>

            {/* Content */}
            <div style={{
              padding: '30px',
              color: '#2a4252',
              flex: 1
            }}>
              <p style={{
                marginBottom: '15px',
                fontSize: '15px',
                lineHeight: 1.6,
                color: '#2a4252',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}>
                <strong style={{ color: '#dc3545', fontWeight: 700 }}>Warning: This action cannot be undone!</strong>
              </p>
              <p style={{
                marginBottom: '15px',
                fontSize: '15px',
                lineHeight: 1.6,
                color: '#2a4252',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}>
                Deleting your account will permanently remove:
              </p>
              <ul style={{
                margin: '15px 0 20px 0',
                paddingLeft: '20px',
                background: '#f8f9fa',
                padding: '15px 20px 15px 35px',
                borderRadius: '8px',
                borderLeft: '3px solid #dc3545'
              }}>
                <li style={{
                  marginBottom: '8px',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#2a4252',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}>Your user information</li>
                <li style={{
                  marginBottom: '8px',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#2a4252',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}>All payment requests (including paid ones)</li>
                <li style={{
                  marginBottom: '8px',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#2a4252',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}>Your employer/subcontractor information</li>
                <li style={{
                  marginBottom: '8px',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#2a4252',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}>All relationships and connections</li>
                {localUser && (localUser.current_role === 'contractor' || (localUser.available_roles && localUser.available_roles.includes('contractor'))) && (
                  <>
                    <li style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#2a4252',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}>All subcontractor relationships</li>
                    <li style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#2a4252',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}>All HMRC CIS submissions</li>
                    <li style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#2a4252',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}>All sub-users under your account</li>
                    <li style={{
                      marginBottom: '8px',
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#2a4252',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}>Subcontractors will still see their payment requests, but your information will be removed</li>
                  </>
                )}
              </ul>
              <p style={{
                marginBottom: '15px',
                marginTop: '20px',
                fontSize: '15px',
                lineHeight: 1.6,
                color: '#2a4252',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}>
                To confirm, please enter your password:
              </p>
              <input
                type="password"
                placeholder="Enter your password"
                value={deletePassword}
                onChange={(e) => {
                  setDeletePassword(e.target.value);
                  setError(''); // Clear error when user types
                }}
                autoComplete="current-password"
                disabled={deleting}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && deletePassword && !deleting) {
                    handleDeleteAccount();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 15px',
                  border: error ? '2px solid #dc3545' : '2px solid #dc3545',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#2a4252',
                  backgroundColor: '#ffffff',
                  marginTop: '10px',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  boxSizing: 'border-box'
                }}
              />
              {error && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 15px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  color: '#856404',
                  fontSize: '14px',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  lineHeight: 1.5
                }}>
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '15px',
              padding: '20px 30px 30px 30px',
              borderTop: '1px solid #e0e0e0',
              background: '#ffffff',
              borderBottomLeftRadius: '15px',
              borderBottomRightRadius: '15px'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setError('');
                }}
                disabled={deleting}
                style={{
                  padding: '12px 25px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  background: '#6c757d',
                  color: '#ffffff',
                  transition: 'all 0.3s ease',
                  opacity: deleting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!deleting) {
                    e.target.style.background = '#5a6268';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleting) {
                    e.target.style.background = '#6c757d';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || !deletePassword}
                style={{
                  padding: '12px 25px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: (deleting || !deletePassword) ? 'not-allowed' : 'pointer',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  background: (deleting || !deletePassword) ? '#e0a0a0' : 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: '#ffffff',
                  transition: 'all 0.3s ease',
                  opacity: (deleting || !deletePassword) ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!deleting && deletePassword) {
                    e.target.style.background = 'linear-gradient(135deg, #c82333, #bd2130)';
                    e.target.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!deleting && deletePassword) {
                    e.target.style.background = 'linear-gradient(135deg, #dc3545, #c82333)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcontractorProfile;